// One booking, however many Clients it carries.
//
// A booking for two to four Clients (CORE-01A) is stored as one `bookings` row
// per Client sharing `booking_group_id` (0005). Every list that drew one card
// per row therefore showed a booking for four as four bookings — on the
// client's Bookings tab, the therapist's job list and the admin tables alike.
// These two rules are the whole of "fold rows into a booking", kept in one
// place so the three apps cannot disagree about what a group is or where it
// stands.

import type { BookingStatus } from "./booking-transitions.ts";

/** A booking's rows — never empty, so `[lead]` is always a row. */
export type BookingRows<T> = [T, ...T[]];

/**
 * Fold rows into bookings, one array per booking.
 *
 * A booking sits where its first row did, so a list the caller already sorted
 * stays sorted. Within a booking the rows keep the caller's order unless
 * `compareRows` is given. A row with no group id is its own booking — two
 * ungrouped rows are never merged.
 */
export function groupRowsByBooking<T extends { id: string }>(
  rows: readonly T[],
  groupIdOf: (row: T) => string | null | undefined,
  compareRows?: (a: T, b: T) => number,
): BookingRows<T>[] {
  const groups = new Map<string, BookingRows<T>>();
  for (const row of rows) {
    const key = groupIdOf(row) ?? `booking:${row.id}`;
    const members = groups.get(key);
    if (members) members.push(row);
    else groups.set(key, [row]);
  }
  const bookings = [...groups.values()];
  if (compareRows) for (const members of bookings) members.sort(compareRows);
  return bookings;
}

/**
 * Id order — the order `settle_payment_job` (0148) walks a group in. Every row
 * of a group is inserted in one transaction, so `created_at` ties and id
 * decides; the first is the REPRESENTATIVE row the group's payment points at.
 */
export function byRowId(a: { id: string }, b: { id: string }): number {
  return a.id.localeCompare(b.id);
}

/** Lifecycle order of the statuses a live booking passes through. */
const OPEN_STATUS_ORDER: readonly string[] = [
  "pending",
  "awaiting_therapist",
  "awaiting_rider",
  "dispatched",
  "en_route_to_client",
  "arrived",
  "in_session",
] satisfies readonly BookingStatus[];

/**
 * One status for the whole booking.
 *
 * Open while ANY Client is still open — the booking is not over until the last
 * session is — showing the most advanced of them. An open status this list
 * does not know yet still counts as open rather than letting the booking fall
 * into history. Once every row is closed: Completed if anyone was served,
 * Cancelled only when every Client was.
 */
export function bookingGroupStatus(statuses: readonly string[]): string {
  const open = statuses.filter((status) => status !== "completed" && status !== "cancelled");
  if (open.length > 0) {
    return open.reduce((best, status) =>
      OPEN_STATUS_ORDER.indexOf(status) > OPEN_STATUS_ORDER.indexOf(best) ? status : best,
    );
  }
  return statuses.includes("completed") ? "completed" : "cancelled";
}
